import { axiosInstance, type CustomResponseFormat } from "api/instance";
import { getCoachToken } from "api/coachAuth";
import {
  normalizeRepeatEntryDays,
  normalizeRepeatEntryTrigger,
  normalizeRepeatIntervalWeeks,
  normalizeTaskEditorTaskType,
  UNCATEGORIZED_SET_NUMBER,
  type TaskEditorState,
  type TaskMetaUpdate,
} from "pages/task-editor/utils/taskEditorUtils";

export type { TaskMetaUpdate };

const TASK_EDITOR_DOMAIN = "task-editor";

const TASK_EDITOR_LIST_ENDPOINT = "task-meta/list"; // 메타 전용 목록 (본문 제외)
const TASK_EDITOR_REMOVE_ENDPOINT = "task/remove";
// 메타 전용 저장 (본문 미포함). 전부 snake_case. CONTRACT-list-meta.md §4.2~4.3 참조.
const TASK_EDITOR_META_SAVE_ENDPOINT = "task-meta/save"; // 단건
const TASK_EDITOR_META_SAVE_ALL_ENDPOINT = "task-meta/save-all"; // 배치(변경분만)
const TASK_EDITOR_DETAIL_ENDPOINT = "task/detail"; // 단건 상세 (메타 + content)
const TASK_EDITOR_CONTENT_COMMIT_ENDPOINT = "task-content/commit"; // 본문만 저장
const TASK_EDITOR_AUTH_HEADER_KEY = "X-Auth-Token";
const TASK_EDITOR_ACCESS_TOKEN_REQUIRED_ERROR =
  "taskEditorApi requires user access token.";

// task-meta/list 응답 항목 — flat 메타 (snake_case, 본문 없음).
interface TaskMetaListItem {
  task_id: string;
  title?: string;
  set_number?: number;
  is_key_activity?: boolean;
  sort_order?: number;
  task_type?: string;
  prerequisite_task_id?: string;
  linked_tool_task_id?: string;
  repeat_interval_weeks?: number;
  repeat_entry_trigger?: string;
  repeat_entry_days?: number;
}

// task/detail 응답 — 메타(top-level, snake_case) + content(본문, { blocks, ... }).
// content 는 blocks 외 필드(chapters 등)도 가질 수 있어 통째로 보존한다.
export interface TaskDetailResult {
  task_id: string;
  title: string;
  set_number: number;
  is_key_activity: boolean;
  sort_order: number;
  content: Record<string, unknown> | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function unwrapResponsePayload<T>(
  response: CustomResponseFormat<T> | undefined
): unknown {
  if (!response) {
    return undefined;
  }

  const result = response.result;
  if (isRecord(result) && "data" in result) {
    return (result as Record<string, unknown>).data;
  }
  if (result !== undefined) {
    return result;
  }
  return response.data;
}

// task-meta/list → task_id 별 TaskEditorState 맵. 본문(blocks)은 비운다 —
// 활동지 선택 시 task/detail 로 채운다.
function normalizeTaskMetaListResponse(
  payload: unknown
): Record<string, TaskEditorState> {
  if (!Array.isArray(payload)) return {};

  return payload.reduce<Record<string, TaskEditorState>>((acc, raw) => {
    if (!isRecord(raw)) return acc;
    const item = raw as unknown as TaskMetaListItem;
    const taskId = typeof item.task_id === "string" ? item.task_id.trim() : "";
    if (!taskId) return acc;

    acc[taskId] = {
      task_id: taskId,
      title: typeof item.title === "string" ? item.title : "",
      set_number:
        typeof item.set_number === "number" && Number.isFinite(item.set_number)
          ? item.set_number
          : UNCATEGORIZED_SET_NUMBER,
      is_key_activity: item.is_key_activity === true ? true : undefined,
      sort_order:
        typeof item.sort_order === "number" && Number.isFinite(item.sort_order)
          ? item.sort_order
          : undefined,
      task_type: normalizeTaskEditorTaskType(item.task_type),
      prerequisite_task_id:
        typeof item.prerequisite_task_id === "string" &&
        item.prerequisite_task_id
          ? item.prerequisite_task_id
          : undefined,
      linked_tool_task_id:
        typeof item.linked_tool_task_id === "string" && item.linked_tool_task_id
          ? item.linked_tool_task_id
          : undefined,
      repeat_interval_weeks: normalizeRepeatIntervalWeeks(
        item.repeat_interval_weeks
      ),
      repeat_entry_trigger: normalizeRepeatEntryTrigger(
        item.repeat_entry_trigger
      ),
      repeat_entry_days: normalizeRepeatEntryDays(item.repeat_entry_days),
      blocks: [],
    };
    return acc;
  }, {});
}

function getTaskEditorAccessTokenFromStorage() {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) {
    return undefined;
  }

  try {
    const parsedUser = JSON.parse(storedUser);
    if (
      isRecord(parsedUser) &&
      typeof parsedUser.accessToken === "string" &&
      parsedUser.accessToken.trim().length > 0
    ) {
      return parsedUser.accessToken;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function getTaskEditorAccessTokenFromAxiosDefaults() {
  const headerToken =
    axiosInstance.defaults.headers?.[TASK_EDITOR_AUTH_HEADER_KEY];

  return typeof headerToken === "string" && headerToken.trim().length > 0
    ? headerToken
    : undefined;
}

function requireTaskEditorAccessToken() {
  const accessToken =
    getTaskEditorAccessTokenFromStorage() ??
    getTaskEditorAccessTokenFromAxiosDefaults();

  if (!accessToken) {
    throw new Error(TASK_EDITOR_ACCESS_TOKEN_REQUIRED_ERROR);
  }

  return accessToken;
}

/** 코치 JWT 세션이면 Bearer, 아니면 레거시 X-Auth-Token. */
export function getTaskEditorAuthHeaders(): Record<string, string> {
  const coachToken = getCoachToken();
  if (coachToken) return { Authorization: `Bearer ${coachToken}` };
  return { [TASK_EDITOR_AUTH_HEADER_KEY]: requireTaskEditorAccessToken() };
}

function getTaskEditorAuthConfig(config?: {
  params?: Record<string, unknown>;
  headers?: Record<string, unknown>;
}) {
  const headers = {
    ...(config?.headers ?? {}),
    ...getTaskEditorAuthHeaders(),
  } as Record<string, string>;

  return { ...config, headers };
}

export const taskEditorApi = {
  getTasksByTaskId: async (): Promise<Record<string, TaskEditorState>> => {
    const response = await axiosInstance.get<
      CustomResponseFormat<TaskMetaListItem[]>
    >(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_LIST_ENDPOINT}`,
      getTaskEditorAuthConfig()
    );

    return normalizeTaskMetaListResponse(unwrapResponsePayload(response));
  },

  removeTask: async (task_id: string) => {
    if (!task_id) return;
    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_REMOVE_ENDPOINT}`,
      undefined,
      getTaskEditorAuthConfig({
        params: { task_id },
      })
    );
  },

  // 메타 단건 저장 (본문 불변). 그 활동지의 전체 메타를 전송 (snake_case).
  // 메타 모달(제목/세트/key)·개별 메타 변경용.
  saveTaskMeta: async (meta: TaskMetaUpdate) => {
    if (!meta?.task_id) return;
    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_META_SAVE_ENDPOINT}`,
      meta,
      getTaskEditorAuthConfig()
    );
  },

  // 메타 배치 저장 (변경된 활동지만). 본문 불변. 드래그 재정렬용.
  saveAllTaskMeta: async (updates: TaskMetaUpdate[]) => {
    if (!Array.isArray(updates) || updates.length === 0) return;
    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_META_SAVE_ALL_ENDPOINT}`,
      { updates },
      getTaskEditorAuthConfig()
    );
  },

  // 단건 상세 — 메타 + content(본문). 목록에서 활동지 선택 시 본문 로드용.
  getTaskDetail: async (
    task_id: string
  ): Promise<TaskDetailResult | undefined> => {
    if (!task_id) return undefined;
    const response = await axiosInstance.get<
      CustomResponseFormat<TaskDetailResult>
    >(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_DETAIL_ENDPOINT}`,
      getTaskEditorAuthConfig({ params: { task_id } })
    );
    const payload = unwrapResponsePayload(response);
    return isRecord(payload)
      ? (payload as unknown as TaskDetailResult)
      : undefined;
  },

  // 본문만 저장 (생성 + blocks). content 래퍼로 전송, task_id 는 쿼리.
  commitContent: async (task_id: string, content: Record<string, unknown>) => {
    if (!task_id) return;
    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_CONTENT_COMMIT_ENDPOINT}`,
      { content },
      getTaskEditorAuthConfig({ params: { task_id } })
    );
  },
};
